import { Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';

import { AccountGate } from '../app/AccountGate';
import { AdminLayout } from '../app/layouts/AdminLayout';
import { InstituteLayout } from '../app/layouts/InstituteLayout';
import { StudentLayout } from '../app/layouts/StudentLayout';
import { RequireRole } from '../app/RequireRole';
import { RootLayout } from '../app/RootLayout';
import { RouteErrorBoundary } from '../app/RouteErrorBoundary';
import { AdminInstituteDetailPage } from '../features/admin/InstituteDetailPage';
import { AdminInstitutesPage } from '../features/admin/InstitutesPage';
import { AdminInstitutesQueuePage } from '../features/admin/InstitutesQueuePage';
import { AdminSuperadminsPage } from '../features/admin/SuperadminsPage';
import { AdminSectionEditorPage } from '../features/admin/tests/SectionEditorPage';
import { AdminTestEditorPage } from '../features/admin/tests/TestEditorPage';
import { AdminTestsListPage } from '../features/admin/tests/TestsListPage';
import { AuthCallbackPage } from '../features/auth/AuthCallbackPage';
import { CheckInboxPage } from '../features/auth/CheckInboxPage';
import { ForgotPasswordPage } from '../features/auth/ForgotPasswordPage';
import { InstituteLoginPage } from '../features/auth/InstituteLoginPage';
import { LoginPage } from '../features/auth/LoginPage';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage';
import { SignupPage } from '../features/auth/SignupPage';
import { VerifyPhonePage } from '../features/auth/VerifyPhonePage';
import { InstituteDashboardPage } from '../features/institute/DashboardPage';
import { InstituteSettingsPage } from '../features/institute/SettingsPage';
import { InstituteStudentsPage } from '../features/institute/StudentsPage';
import { RegisterInstitutePage } from '../features/public/RegisterInstitutePage';
import { StudentHomePage } from '../features/student/HomePage';
import { lazyWithRetry } from '../shared/lib/lazy-with-retry';
import { PageLoader } from '../shared/ui/PageLoader';

import { AppHomePage } from './AppHomePage';
import { HomePage } from './HomePage';
import { NotFoundPage } from './NotFoundPage';

const InstituteBatchesPage = lazyWithRetry(() =>
  import('../features/institute/BatchesPage').then((m) => ({ default: m.InstituteBatchesPage })),
);
const SessionsListPage = lazyWithRetry(() =>
  import('../features/institute/SessionsListPage').then((m) => ({ default: m.SessionsListPage })),
);
const NewSessionPage = lazyWithRetry(() =>
  import('../features/institute/NewSessionPage').then((m) => ({ default: m.NewSessionPage })),
);
const SessionDetailPage = lazyWithRetry(() =>
  import('../features/institute/SessionDetailPage').then((m) => ({
    default: m.SessionDetailPage,
  })),
);
const AttendanceHubPage = lazyWithRetry(() =>
  import('../features/institute/AttendanceHubPage').then((m) => ({
    default: m.AttendanceHubPage,
  })),
);
const BatchAttendancePage = lazyWithRetry(() =>
  import('../features/institute/BatchAttendancePage').then((m) => ({
    default: m.BatchAttendancePage,
  })),
);
const BatchSchedulePage = lazyWithRetry(() =>
  import('../features/institute/BatchSchedulePage').then((m) => ({
    default: m.BatchSchedulePage,
  })),
);
const InstituteStaffPage = lazyWithRetry(() =>
  import('../features/institute/StaffPage').then((m) => ({ default: m.InstituteStaffPage })),
);
const WorkQueuePage = lazyWithRetry(() =>
  import('../features/institute/WorkQueuePage').then((m) => ({ default: m.WorkQueuePage })),
);
const InstituteMessagesPage = lazyWithRetry(() =>
  import('../features/institute/MessagesPage').then((m) => ({ default: m.InstituteMessagesPage })),
);
const InstituteMessageThreadPage = lazyWithRetry(() =>
  import('../features/institute/MessageThreadPage').then((m) => ({
    default: m.InstituteMessageThreadPage,
  })),
);
const WorkflowBoardsPage = lazyWithRetry(() =>
  import('../features/institute/WorkflowBoardsPage').then((m) => ({
    default: m.WorkflowBoardsPage,
  })),
);
const ChecklistPacksPage = lazyWithRetry(() =>
  import('../features/institute/ChecklistPacksPage').then((m) => ({
    default: m.ChecklistPacksPage,
  })),
);
const CardPage = lazyWithRetry(() =>
  import('../features/institute/CardPage').then((m) => ({
    default: m.CardPage,
  })),
);
const StageSettingsPage = lazyWithRetry(() =>
  import('../features/institute/StageSettingsPage').then((m) => ({
    default: m.StageSettingsPage,
  })),
);
const WorkflowBoardPage = lazyWithRetry(() =>
  import('../features/institute/WorkflowBoardPage').then((m) => ({
    default: m.WorkflowBoardPage,
  })),
);
const StudentSkillPracticeHubPage = lazyWithRetry(() =>
  import('../features/student/SkillPracticeHubPage').then((m) => ({
    default: m.SkillPracticeHubPage,
  })),
);
const StudentPracticeSetListPage = lazyWithRetry(() =>
  import('../features/student/PracticeSetListPage').then((m) => ({
    default: m.PracticeSetListPage,
  })),
);
const StudentMistakesPage = lazyWithRetry(() =>
  import('../features/student/MistakesPage').then((m) => ({ default: m.MistakesPage })),
);
const StudentDrillReviewPage = lazyWithRetry(() =>
  import('../features/student/DrillReviewPage').then((m) => ({
    default: m.DrillReviewPage,
  })),
);
const StudentSessionsPage = lazyWithRetry(() =>
  import('../features/student/SessionsListPage').then((m) => ({
    default: m.StudentSessionsPage,
  })),
);
const StudentAssignmentsPage = lazyWithRetry(() =>
  import('../features/student/AssignmentsPage').then((m) => ({
    default: m.StudentAssignmentsPage,
  })),
);
const StudentResultsPage = lazyWithRetry(() =>
  import('../features/student/ResultsPage').then((m) => ({ default: m.StudentResultsPage })),
);
const StudentResultsListPage = lazyWithRetry(() =>
  import('../features/student/ResultsListPage').then((m) => ({
    default: m.StudentResultsListPage,
  })),
);
const StudentWritingFeedbackPage = lazyWithRetry(() =>
  import('../features/student/WritingFeedbackPage').then((m) => ({
    default: m.WritingFeedbackPage,
  })),
);
const StudentProgressPage = lazyWithRetry(() =>
  import('../features/student/ProgressPage').then((m) => ({ default: m.StudentProgressPage })),
);
// One component, three role-scoped mounts — the same shape EmailPreferencesPage
// already uses, because a person's own account is the same screen whoever they
// are.
const AccountProfilePage = lazyWithRetry(() =>
  import('../features/account/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const StudentMorePage = lazyWithRetry(() =>
  import('../features/student/MorePage').then((m) => ({ default: m.StudentMorePage })),
);
const PracticeBrowsePage = lazyWithRetry(() =>
  import('../features/student/PracticeBrowsePage').then((m) => ({ default: m.PracticeBrowsePage })),
);
const StudentContinuePage = lazyWithRetry(() =>
  import('../features/student/ContinuePage').then((m) => ({ default: m.StudentContinuePage })),
);
const VocabBooksPage = lazyWithRetry(() =>
  import('../features/student/VocabBooksPage').then((m) => ({ default: m.VocabBooksPage })),
);
const VocabBookDetailPage = lazyWithRetry(() =>
  import('../features/student/VocabBookDetailPage').then((m) => ({
    default: m.VocabBookDetailPage,
  })),
);
const StudyMaterialsPage = lazyWithRetry(() =>
  import('../features/student/StudyMaterialsPage').then((m) => ({ default: m.StudyMaterialsPage })),
);
const ShadowingPage = lazyWithRetry(() =>
  import('../features/student/ShadowingPage').then((m) => ({ default: m.ShadowingPage })),
);
const StudentMyActivityPage = lazyWithRetry(() =>
  import('../features/student/MyActivityPage').then((m) => ({ default: m.StudentMyActivityPage })),
);
const StudentDocumentsPage = lazyWithRetry(() =>
  import('../features/student/DocumentsPage').then((m) => ({ default: m.StudentDocumentsPage })),
);
const StudentMessagesPage = lazyWithRetry(() =>
  import('../features/student/MessagesPage').then((m) => ({ default: m.StudentMessagesPage })),
);
const StudentMessageThreadPage = lazyWithRetry(() =>
  import('../features/student/MessageThreadPage').then((m) => ({
    default: m.StudentMessageThreadPage,
  })),
);
const StudyPlanPage = lazyWithRetry(() =>
  import('../features/student/StudyPlanPage').then((m) => ({ default: m.StudyPlanPage })),
);
const ResultAnalysisPage = lazyWithRetry(() =>
  import('../features/student/ResultAnalysisPage').then((m) => ({ default: m.ResultAnalysisPage })),
);
// Shared by the student and institute workspaces — the rows differ, the page
// doesn't.
const EmailPreferencesPage = lazyWithRetry(() =>
  import('../features/notifications/EmailPreferencesPage').then((m) => ({
    default: m.EmailPreferencesPage,
  })),
);
const NotificationsPage = lazyWithRetry(() =>
  import('../features/notifications/NotificationsPage').then((m) => ({
    default: m.NotificationsPage,
  })),
);
const InstituteStudentDossierPage = lazyWithRetry(() =>
  import('../features/institute/StudentDossierPage').then((m) => ({
    default: m.InstituteStudentDossierPage,
  })),
);
const AttemptShell = lazyWithRetry(() =>
  import('../features/exam/AttemptShell').then((m) => ({ default: m.AttemptShell })),
);
const PracticeShell = lazyWithRetry(() =>
  import('../features/exam/AttemptShell').then((m) => ({ default: m.PracticeShell })),
);
const PaperReviewPage = lazyWithRetry(() =>
  import('../features/exam/review/PaperReviewPage').then((m) => ({ default: m.PaperReviewPage })),
);
const InstituteGradingQueuePage = lazyWithRetry(() =>
  import('../features/institute/GradingQueuePage').then((m) => ({
    default: m.InstituteGradingQueuePage,
  })),
);
const InstituteResultsPage = lazyWithRetry(() =>
  import('../features/institute/ResultsPage').then((m) => ({
    default: m.InstituteResultsPage,
  })),
);
const InstituteResultDetailPage = lazyWithRetry(() =>
  import('../features/institute/ResultDetailPage').then((m) => ({
    default: m.InstituteResultDetailPage,
  })),
);
const InstituteApprovalPage = lazyWithRetry(() =>
  import('../features/institute/ApprovalPage').then((m) => ({
    default: m.InstituteApprovalPage,
  })),
);
const InstituteBuilderHubPage = lazyWithRetry(() =>
  import('../features/institute/BuilderHubPage').then((m) => ({ default: m.BuilderHubPage })),
);
const InstitutePdfReviewPage = lazyWithRetry(() =>
  import('../features/institute/PdfReviewPage').then((m) => ({ default: m.PdfReviewPage })),
);
const InstituteReadingBuilderPage = lazyWithRetry(() =>
  import('../features/institute/ReadingBuilderPage').then((m) => ({
    default: m.ReadingBuilderPage,
  })),
);
const InstituteListeningBuilderPage = lazyWithRetry(() =>
  import('../features/institute/ListeningBuilderPage').then((m) => ({
    default: m.ListeningBuilderPage,
  })),
);
const InstituteWritingBuilderPage = lazyWithRetry(() =>
  import('../features/institute/WritingBuilderPage').then((m) => ({
    default: m.WritingBuilderPage,
  })),
);
const InstituteSpeakingBuilderPage = lazyWithRetry(() =>
  import('../features/institute/SpeakingBuilderPage').then((m) => ({
    default: m.SpeakingBuilderPage,
  })),
);
const InstituteTestsPage = lazyWithRetry(() =>
  import('../features/institute/TestsPage').then((m) => ({ default: m.TestsPage })),
);
const InstituteFullMockBuilderPage = lazyWithRetry(() =>
  import('../features/institute/FullMockBuilderPage').then((m) => ({
    default: m.FullMockBuilderPage,
  })),
);
const InstitutePracticeSetBuilderPage = lazyWithRetry(() =>
  import('../features/institute/PracticeSetBuilderPage').then((m) => ({
    default: m.PracticeSetBuilderPage,
  })),
);
const InstitutePracticeSetsPage = lazyWithRetry(() =>
  import('../features/institute/PracticeSetsPage').then((m) => ({
    default: m.PracticeSetsPage,
  })),
);
const InstituteAssignmentsPage = lazyWithRetry(() =>
  import('../features/institute/AssignmentsPage').then((m) => ({ default: m.AssignmentsPage })),
);
const InstituteNewAssignmentPage = lazyWithRetry(() =>
  import('../features/institute/NewAssignmentPage').then((m) => ({
    default: m.NewAssignmentPage,
  })),
);
const InstituteAssignmentDetailPage = lazyWithRetry(() =>
  import('../features/institute/AssignmentDetailPage').then((m) => ({
    default: m.AssignmentDetailPage,
  })),
);
const FinanceLayout = lazyWithRetry(() =>
  import('../features/institute/FinanceLayout').then((m) => ({ default: m.FinanceLayout })),
);
const FinanceOverviewPage = lazyWithRetry(() =>
  import('../features/institute/FinanceOverviewPage').then((m) => ({
    default: m.FinanceOverviewPage,
  })),
);
const InvoicesPage = lazyWithRetry(() =>
  import('../features/institute/InvoicesPage').then((m) => ({ default: m.InvoicesPage })),
);
const InvoiceComposerPage = lazyWithRetry(() =>
  import('../features/institute/InvoiceComposerPage').then((m) => ({
    default: m.InvoiceComposerPage,
  })),
);
const InvoiceDetailPage = lazyWithRetry(() =>
  import('../features/institute/InvoiceDetailPage').then((m) => ({
    default: m.InvoiceDetailPage,
  })),
);
const PaymentsPage = lazyWithRetry(() =>
  import('../features/institute/PaymentsPage').then((m) => ({ default: m.PaymentsPage })),
);
const ExpensesPage = lazyWithRetry(() =>
  import('../features/institute/ExpensesPage').then((m) => ({ default: m.ExpensesPage })),
);
const FinanceSettingsPage = lazyWithRetry(() =>
  import('../features/institute/FinanceSettingsPage').then((m) => ({
    default: m.FinanceSettingsPage,
  })),
);
const StudentBillingPage = lazyWithRetry(() =>
  import('../features/student/BillingPage').then((m) => ({ default: m.StudentBillingPage })),
);
const StudentBillingInvoicePage = lazyWithRetry(() =>
  import('../features/student/BillingInvoicePage').then((m) => ({
    default: m.StudentBillingInvoicePage,
  })),
);

const AdminSpeakingStoragePage = lazyWithRetry(() =>
  import('../features/admin/SpeakingStoragePage').then((m) => ({
    default: m.AdminSpeakingStoragePage,
  })),
);

const AdminEmailPage = lazyWithRetry(() =>
  import('../features/admin/email/EmailPage').then((m) => ({
    default: m.AdminEmailPage,
  })),
);

const InstituteEmailPage = lazyWithRetry(() =>
  import('../features/institute/email/InstituteEmailPage').then((m) => ({
    default: m.InstituteEmailPage,
  })),
);

function Lazy({ children }: { children: ReactNode }): JSX.Element {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

/** Phase 157 rename: /institute/homework/:id → /institute/assignments/:id.
 *  A plain <Navigate> cannot interpolate a param, and dropping the id would
 *  send someone who followed a colleague's link to a list instead of the row
 *  they were sent. */
function RedirectToAssignment(): JSX.Element {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  return <Navigate to={`/institute/assignments/${assignmentId ?? ''}`} replace />;
}

// One gate, one loading state. See AccountGate for why RequireAuth +
// RequirePhoneVerified were collapsed into it.
function Authed({ children }: { children: ReactNode }): JSX.Element {
  return <AccountGate>{children}</AccountGate>;
}

export const router = createBrowserRouter([
  {
    // Pathless layout route: hosts the route-aware app chrome (RootLayout) and
    // is the error boundary for the whole tree — including a lazy chunk that
    // fails to load.
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/login', element: <LoginPage /> },
      { path: '/i/:slug/login', element: <InstituteLoginPage /> },
      { path: '/signup', element: <SignupPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      // MUST stay public, and for the same reason as /auth/callback: the
      // recovery link arrives with no session yet, and AccountGate would bounce
      // it to /login and discard the link. It is also the one screen a user with
      // an incomplete profile must reach without passing the profile dialog.
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/register-institute', element: <RegisterInstitutePage /> },
      { path: '/verify-phone', element: <VerifyPhonePage /> },
      // Where signup lands since Phase 184. /verify-phone stays routed: nothing
      // navigates there any more, but an old link should not 404.
      { path: '/check-inbox', element: <CheckInboxPage /> },
      // MUST stay public: the PKCE code exchange is async, so there is a window
      // where the session is still null. Inside AccountGate that window would
      // redirect to /login and discard the code, and Google sign-in would never
      // complete. See AuthCallbackPage.
      { path: '/auth/callback', element: <AuthCallbackPage /> },
      {
        path: '/app',
        element: (
          <Authed>
            <AppHomePage />
          </Authed>
        ),
      },
      {
        path: '/student/exam/:sessionId',
        element: (
          <Authed>
            <RequireRole allow="student">
              <Lazy>
                <AttemptShell />
              </Lazy>
            </RequireRole>
          </Authed>
        ),
      },
      {
        path: '/student/practice/:attemptId',
        element: (
          <Authed>
            <RequireRole allow="student">
              <Lazy>
                <PracticeShell />
              </Lazy>
            </RequireRole>
          </Authed>
        ),
      },
      // Phase 189 — the paper the student sat, read-only, with the marks on it.
      // Outside StudentLayout like the two players above, and matched by
      // isExamRoute() for the same reason: it wears the exam's own chrome, and
      // the app's tab bar and toasts would float on top of it.
      {
        path: '/student/results/:attemptId/paper',
        element: (
          <Authed>
            <RequireRole allow="student">
              <Lazy>
                <PaperReviewPage />
              </Lazy>
            </RequireRole>
          </Authed>
        ),
      },
      {
        path: '/student',
        element: (
          <Authed>
            <RequireRole allow="student">
              <StudentLayout />
            </RequireRole>
          </Authed>
        ),
        children: [
          { index: true, element: <Navigate to="home" replace /> },
          { path: 'home', element: <StudentHomePage /> },
          {
            path: 'sessions',
            element: (
              <Lazy>
                <StudentSessionsPage />
              </Lazy>
            ),
          },
          {
            // The `assignment.assigned` notification links here (see
            // assignment_notify_student). Without this route the link fell
            // through to the `*` catch-all and rendered the public marketing
            // home page, which reads as being logged out.
            path: 'assignments',
            element: (
              <Lazy>
                <StudentAssignmentsPage />
              </Lazy>
            ),
          },
          // Phase 157 renamed the route. Notifications sent before then carry
          // '/student/homework' in the database, and students bookmark pages —
          // both must keep landing somewhere real, so the old path is a
          // permanent redirect rather than a 404.
          { path: 'homework', element: <Navigate to="/student/assignments" replace /> },
          {
            path: 'results',
            element: (
              <Lazy>
                <StudentResultsListPage />
              </Lazy>
            ),
          },
          {
            path: 'results/:attemptId',
            element: (
              <Lazy>
                <StudentResultsPage />
              </Lazy>
            ),
          },
          {
            path: 'results/:attemptId/writing',
            element: (
              <Lazy>
                <StudentWritingFeedbackPage />
              </Lazy>
            ),
          },
          {
            path: 'progress',
            element: (
              <Lazy>
                <StudentProgressPage />
              </Lazy>
            ),
          },
          {
            path: 'more',
            element: (
              <Lazy>
                <StudentMorePage />
              </Lazy>
            ),
          },
          {
            path: 'profile',
            element: (
              <Lazy>
                <AccountProfilePage />
              </Lazy>
            ),
          },
          {
            path: 'notifications',
            element: (
              <Lazy>
                <NotificationsPage />
              </Lazy>
            ),
          },
          {
            path: 'notifications/email',
            element: (
              <Lazy>
                <EmailPreferencesPage />
              </Lazy>
            ),
          },
          {
            path: 'practice',
            element: (
              <Lazy>
                <PracticeBrowsePage />
              </Lazy>
            ),
          },
          // Phase 162 — practice by question type. The bare path and the
          // per-skill path render the SAME component (it reads the param), so
          // lazyWithRetry still gets a prop-less default export.
          {
            path: 'skill-practice',
            element: (
              <Lazy>
                <StudentSkillPracticeHubPage />
              </Lazy>
            ),
          },
          {
            path: 'skill-practice/:skill',
            element: (
              <Lazy>
                <StudentSkillPracticeHubPage />
              </Lazy>
            ),
          },
          {
            path: 'skill-practice/:skill/:typeCode',
            element: (
              <Lazy>
                <StudentPracticeSetListPage />
              </Lazy>
            ),
          },
          {
            path: 'mistakes',
            element: (
              <Lazy>
                <StudentMistakesPage />
              </Lazy>
            ),
          },
          {
            path: 'drills/:attemptId/review',
            element: (
              <Lazy>
                <StudentDrillReviewPage />
              </Lazy>
            ),
          },
          {
            // Phase 159 — where "Save & exit" lands, and where the home card's
            // "See all" goes. Deep-linkable on purpose: a student who closed
            // the tab mid-test can bookmark their way straight back in.
            path: 'continue',
            element: (
              <Lazy>
                <StudentContinuePage />
              </Lazy>
            ),
          },
          {
            path: 'vocab',
            element: (
              <Lazy>
                <VocabBooksPage />
              </Lazy>
            ),
          },
          {
            path: 'vocab/:bookId',
            element: (
              <Lazy>
                <VocabBookDetailPage />
              </Lazy>
            ),
          },
          {
            path: 'materials',
            element: (
              <Lazy>
                <StudyMaterialsPage />
              </Lazy>
            ),
          },
          {
            path: 'shadowing',
            element: (
              <Lazy>
                <ShadowingPage />
              </Lazy>
            ),
          },
          {
            path: 'study-plan',
            element: (
              <Lazy>
                <StudyPlanPage />
              </Lazy>
            ),
          },
          {
            path: 'activity',
            element: (
              <Lazy>
                <StudentMyActivityPage />
              </Lazy>
            ),
          },
          {
            // The `document.*` notifications link here (phase 174).
            path: 'documents',
            element: (
              <Lazy>
                <StudentDocumentsPage />
              </Lazy>
            ),
          },
          {
            path: 'messages',
            element: (
              <Lazy>
                <StudentMessagesPage />
              </Lazy>
            ),
          },
          {
            // The `message.received` notification links straight here, so it
            // must resolve without passing through the list (phase 179a).
            path: 'messages/:threadId',
            element: (
              <Lazy>
                <StudentMessageThreadPage />
              </Lazy>
            ),
          },
          {
            path: 'billing',
            element: (
              <Lazy>
                <StudentBillingPage />
              </Lazy>
            ),
          },
          {
            // The invoice.issued / invoice.overdue notifications link straight
            // here, so it must resolve without passing through the list.
            path: 'billing/:invoiceId',
            element: (
              <Lazy>
                <StudentBillingInvoicePage />
              </Lazy>
            ),
          },
          {
            path: 'results/:attemptId/analysis',
            element: (
              <Lazy>
                <ResultAnalysisPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        path: '/institute',
        element: (
          <Authed>
            <RequireRole allow="institute">
              <InstituteLayout />
            </RequireRole>
          </Authed>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: <InstituteDashboardPage /> },
          {
            path: 'work',
            element: (
              <Lazy>
                <WorkQueuePage />
              </Lazy>
            ),
          },
          {
            path: 'messages',
            element: (
              <Lazy>
                <InstituteMessagesPage />
              </Lazy>
            ),
          },
          {
            // Where the `message.received` notification points for staff.
            path: 'messages/:threadId',
            element: (
              <Lazy>
                <InstituteMessageThreadPage />
              </Lazy>
            ),
          },
          { path: 'students', element: <InstituteStudentsPage /> },
          {
            // The Phase 138 follow-up notification links at
            // /institute/students/<id>; the page itself lives one segment
            // deeper. Without this the link 404'd to the catch-all.
            path: 'students/:studentId',
            element: <Navigate to="dossier" replace />,
          },
          {
            path: 'students/:studentId/dossier',
            element: (
              <Lazy>
                <InstituteStudentDossierPage />
              </Lazy>
            ),
          },
          {
            path: 'attendance',
            element: (
              <Lazy>
                <AttendanceHubPage />
              </Lazy>
            ),
          },
          {
            path: 'batches',
            element: (
              <Lazy>
                <InstituteBatchesPage />
              </Lazy>
            ),
          },
          {
            path: 'batches/:batchId/attendance',
            element: (
              <Lazy>
                <BatchAttendancePage />
              </Lazy>
            ),
          },
          {
            path: 'batches/:batchId/schedule',
            element: (
              <Lazy>
                <BatchSchedulePage />
              </Lazy>
            ),
          },
          {
            path: 'sessions',
            element: (
              <Lazy>
                <SessionsListPage />
              </Lazy>
            ),
          },
          {
            path: 'sessions/new',
            element: (
              <Lazy>
                <NewSessionPage />
              </Lazy>
            ),
          },
          {
            path: 'sessions/:sessionId',
            element: (
              <Lazy>
                <SessionDetailPage />
              </Lazy>
            ),
          },
          {
            path: 'results',
            element: (
              <Lazy>
                <InstituteResultsPage />
              </Lazy>
            ),
          },
          {
            path: 'results/:attemptId',
            element: (
              <Lazy>
                <InstituteResultDetailPage />
              </Lazy>
            ),
          },
          {
            path: 'grading',
            element: (
              <Lazy>
                <InstituteGradingQueuePage />
              </Lazy>
            ),
          },
          {
            path: 'grading/:attemptId',
            element: (
              <Lazy>
                <InstituteApprovalPage />
              </Lazy>
            ),
          },
          {
            path: 'assignments',
            element: (
              <Lazy>
                <InstituteAssignmentsPage />
              </Lazy>
            ),
          },
          {
            path: 'assignments/new',
            element: (
              <Lazy>
                <InstituteNewAssignmentPage />
              </Lazy>
            ),
          },
          {
            path: 'assignments/:assignmentId',
            element: (
              <Lazy>
                <InstituteAssignmentDetailPage />
              </Lazy>
            ),
          },
          // Phase 157 rename — see the student redirect above. Staff paste
          // these links to each other, so they outlive the rename too.
          { path: 'homework', element: <Navigate to="/institute/assignments" replace /> },
          { path: 'homework/new', element: <Navigate to="/institute/assignments/new" replace /> },
          { path: 'homework/:assignmentId', element: <RedirectToAssignment /> },
          {
            path: 'tests',
            element: (
              <Lazy>
                <InstituteTestsPage />
              </Lazy>
            ),
          },
          {
            path: 'builder',
            element: (
              <Lazy>
                <InstituteBuilderHubPage />
              </Lazy>
            ),
          },
          {
            path: 'builder/review/:id',
            element: (
              <Lazy>
                <InstitutePdfReviewPage />
              </Lazy>
            ),
          },
          {
            path: 'builder/reading',
            element: (
              <Lazy>
                <InstituteReadingBuilderPage />
              </Lazy>
            ),
          },
          {
            path: 'builder/listening',
            element: (
              <Lazy>
                <InstituteListeningBuilderPage />
              </Lazy>
            ),
          },
          {
            path: 'builder/writing',
            element: (
              <Lazy>
                <InstituteWritingBuilderPage />
              </Lazy>
            ),
          },
          {
            path: 'builder/speaking',
            element: (
              <Lazy>
                <InstituteSpeakingBuilderPage />
              </Lazy>
            ),
          },
          {
            path: 'builder/full-test',
            element: (
              <Lazy>
                <InstituteFullMockBuilderPage />
              </Lazy>
            ),
          },
          {
            path: 'builder/practice-sets',
            element: (
              <Lazy>
                <InstitutePracticeSetBuilderPage />
              </Lazy>
            ),
          },
          {
            path: 'practice-sets',
            element: (
              <Lazy>
                <InstitutePracticeSetsPage />
              </Lazy>
            ),
          },
          // The builder is the one way in, so a "new" bookmark lands there
          // rather than on the catch-all.
          {
            path: 'practice-sets/new',
            element: <Navigate to="/institute/builder/practice-sets" replace />,
          },
          // Retired entry points (phase 124). Keep the paths so a bookmark
          // lands on the hub instead of falling through to the catch-all and
          // dumping a logged-in teacher on the marketing home page.
          { path: 'builder/new', element: <Navigate to="/institute/builder" replace /> },
          { path: 'builder/quick-block', element: <Navigate to="/institute/builder" replace /> },
          {
            path: 'builder/generate',
            element: <Navigate to="/institute/builder/reading" replace />,
          },
          {
            // One layout route with the section's own tab bar; every child
            // renders inside it, so the tabs and the "Record a payment" button
            // survive navigation between them.
            path: 'finance',
            element: (
              <Lazy>
                <FinanceLayout />
              </Lazy>
            ),
            children: [
              { index: true, element: <FinanceOverviewPage /> },
              { path: 'invoices', element: <InvoicesPage /> },
              // Ahead of :invoiceId, or "new" is read as an id and 404s
              // against a uuid lookup.
              { path: 'invoices/new', element: <InvoiceComposerPage /> },
              { path: 'invoices/:invoiceId', element: <InvoiceDetailPage /> },
              { path: 'invoices/:invoiceId/edit', element: <InvoiceComposerPage /> },
              { path: 'payments', element: <PaymentsPage /> },
              { path: 'expenses', element: <ExpensesPage /> },
              { path: 'settings', element: <FinanceSettingsPage /> },
            ],
          },
          {
            path: 'workflow',
            element: (
              <Lazy>
                <WorkflowBoardsPage />
              </Lazy>
            ),
          },
          // Before the :boardId route, or "checklists" would be read as a
          // board id and 404 against a uuid lookup.
          {
            path: 'workflow/checklists',
            element: (
              <Lazy>
                <ChecklistPacksPage />
              </Lazy>
            ),
          },
          {
            path: 'workflow/checklists/:packId',
            element: (
              <Lazy>
                <ChecklistPacksPage />
              </Lazy>
            ),
          },
          // Both of these sit under :boardId in the URL but ahead of it in this
          // list: React Router matches the more specific path first either way,
          // and keeping them adjacent is how the next person notices they exist.
          {
            path: 'workflow/:boardId/card/:cardId',
            element: (
              <Lazy>
                <CardPage />
              </Lazy>
            ),
          },
          {
            path: 'workflow/:boardId/stage/:stageId',
            element: (
              <Lazy>
                <StageSettingsPage />
              </Lazy>
            ),
          },
          {
            path: 'workflow/:boardId',
            element: (
              <Lazy>
                <WorkflowBoardPage />
              </Lazy>
            ),
          },
          {
            path: 'staff',
            element: (
              <Lazy>
                <InstituteStaffPage />
              </Lazy>
            ),
          },
          { path: 'settings', element: <InstituteSettingsPage /> },
          {
            path: 'settings/email',
            element: (
              <Lazy>
                <InstituteEmailPage />
              </Lazy>
            ),
          },
          {
            path: 'profile',
            element: (
              <Lazy>
                <AccountProfilePage />
              </Lazy>
            ),
          },
          {
            path: 'notifications',
            element: (
              <Lazy>
                <NotificationsPage />
              </Lazy>
            ),
          },
          {
            path: 'notifications/email',
            element: (
              <Lazy>
                <EmailPreferencesPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        path: '/admin',
        element: (
          <Authed>
            <RequireRole allow="superadmin">
              <AdminLayout />
            </RequireRole>
          </Authed>
        ),
        children: [
          { index: true, element: <Navigate to="institutes" replace /> },
          {
            path: 'profile',
            element: (
              <Lazy>
                <AccountProfilePage />
              </Lazy>
            ),
          },
          { path: 'institutes', element: <AdminInstitutesPage /> },
          { path: 'institutes/queue', element: <AdminInstitutesQueuePage /> },
          { path: 'institutes/:id', element: <AdminInstituteDetailPage /> },
          { path: 'superadmins', element: <AdminSuperadminsPage /> },
          {
            path: 'email',
            element: (
              <Lazy>
                <AdminEmailPage />
              </Lazy>
            ),
          },
          {
            path: 'speaking-storage',
            element: (
              <Lazy>
                <AdminSpeakingStoragePage />
              </Lazy>
            ),
          },
          { path: 'tests', element: <AdminTestsListPage /> },
          { path: 'tests/:testId', element: <AdminTestEditorPage /> },
          { path: 'tests/:testId/sections/:sectionId', element: <AdminSectionEditorPage /> },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
